import { useCallback, useEffect, useRef, useState } from 'react';
import { PARTS, serializeParts, parseRemote, reconcile, buildSave } from './assessmentSync.js';

// Keeps the assessment's analysis and sketches in Salesforce as well as in the
// browser. The rules live in assessmentSync.js; this is only the plumbing.
//
// Opening the page never writes on its own account. It writes only what this
// device changed and never saved, or what this device holds that Salesforce
// has never had. A read-only viewer reads and never writes, and the sample
// household never touches Salesforce at all (the caller passes enabled=false).

const ENDPOINT = '/api/FinancialProfileAssessmentService';
const DEBOUNCE_MS = 1500;
const RETRY_MS = 30000;

export default function useAssessmentSync({ leadId, enabled, readOnly, assessment, update }) {
  const [sync, setSync] = useState(null);

  const assessmentRef = useRef(assessment);
  assessmentRef.current = assessment;
  const loadedRef = useRef(false);
  const profileExistsRef = useRef(true);
  const queuedRef = useRef(new Set());
  const inFlightRef = useRef(false);
  const timerRef = useRef(null);
  const retryRef = useRef(null);
  const saveRef = useRef(() => {});

  const active = Boolean(enabled && leadId);

  const schedule = useCallback((delay = DEBOUNCE_MS) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveRef.current(), delay);
  }, []);

  saveRef.current = async () => {
    if (!active || readOnly || !loadedRef.current || inFlightRef.current) return;
    const parts = [...queuedRef.current];
    if (!parts.length) return;
    queuedRef.current.clear();

    if (!profileExistsRef.current) {
      setSync({ state: 'waiting' });
      return;
    }

    const { payload, sending, oversized } = buildSave(leadId, serializeParts(assessmentRef.current), parts);
    if (!sending.length) {
      if (oversized.length) setSync({ state: 'too-large', parts: oversized });
      return;
    }

    inFlightRef.current = true;
    setSync({ state: 'saving' });
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        for (const p of sending) update(`assessment.pendingSync.${p}`, false);
        setSync(oversized.length ? { state: 'too-large', parts: oversized } : { state: 'saved' });
      } else if (res.status === 404) {
        // No Financial Profile yet: the questionnaire has never been saved.
        profileExistsRef.current = false;
        setSync({ state: 'waiting' });
      } else if (res.status === 413) {
        let field = null;
        try { field = (await res.json()).field; } catch { /* body not json */ }
        const part = Object.keys(PARTS).find(p => PARTS[p].field === field);
        setSync({ state: 'too-large', parts: part ? [part] : sending });
      } else {
        throw new Error(`save ${res.status}`);
      }
    } catch {
      // Still pending on the device, so the next open retries too. One
      // automatic retry here covers a blip without hammering an outage.
      setSync({ state: 'error' });
      clearTimeout(retryRef.current);
      retryRef.current = setTimeout(() => {
        for (const p of sending) queuedRef.current.add(p);
        saveRef.current();
      }, RETRY_MS);
    } finally {
      inFlightRef.current = false;
      if (queuedRef.current.size) schedule();
    }
  };

  // Load once per household.
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    loadedRef.current = false;
    (async () => {
      try {
        const res = await fetch(`${ENDPOINT}?leadId=${encodeURIComponent(leadId)}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`load ${res.status}`);
        const body = await res.json();
        if (cancelled) return;

        const remote = parseRemote(body);
        profileExistsRef.current = remote.profileExists;
        const actions = reconcile({
          local: serializeParts(assessmentRef.current),
          remote: remote.strings,
          pending: assessmentRef.current?.pendingSync || {},
          profileExists: remote.profileExists,
          readOnly,
        });
        for (const [part, action] of Object.entries(actions)) {
          if (action === 'adopt') update(`assessment.${PARTS[part].store}`, remote.values[part]);
          if (action === 'push') queuedRef.current.add(part);
        }
        loadedRef.current = true;

        const values = Object.values(actions);
        if (values.includes('waiting')) setSync({ state: 'waiting' });
        else if (queuedRef.current.size) saveRef.current();
        else if (!readOnly && Object.values(remote.strings).some(Boolean)) setSync({ state: 'saved' });
      } catch {
        if (cancelled) return;
        // Salesforce could not be reached: show this device's copy, and let a
        // change made now still be saved, but push nothing speculatively.
        loadedRef.current = true;
        setSync({ state: 'offline' });
      }
    })();
    return () => {
      cancelled = true;
    };
    // `update` and `readOnly` are stable for the life of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, leadId]);

  // Leaving the page flushes anything still waiting on the debounce. If it does
  // not make it, the part is still marked pending and the next open sends it.
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    clearTimeout(retryRef.current);
    if (queuedRef.current.size) saveRef.current();
  }, []);

  const markChanged = useCallback(part => {
    if (!active || readOnly) return;
    update(`assessment.pendingSync.${part}`, true);
    queuedRef.current.add(part);
    schedule();
  }, [active, readOnly, update, schedule]);

  return { sync: active ? sync : null, markChanged };
}
